import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase, Users, TrendingUp, ArrowRight, Search, Compass, FileText, Award } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const HomePage = () => {
  const steps = [
    { label: 'Discover', description: 'Explore premium opportunities', icon: Compass, href: '/jobs' },
    { label: 'Client', description: 'Access to explore amazing talent', icon: Users, href: '/contact' },
  ];

  const features = [
    {
      title: 'Expert Recruitment',
      description: 'Our team of experienced recruiters connects top talent with leading companies worldwide.',
      icon: Users
    },
    {
      title: 'Industry Leading',
      description: 'We specialize in tech, digital business and more. Find your perfect career match.',
      icon: Briefcase
    },
    {
      title: 'Fast Placement',
      description: 'We value your time and career progression.',
      icon: TrendingUp
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      <section className="relative ocean-pattern py-32 px-6 md:px-12 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-950"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >


            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6" data-testid="hero-heading">
              Navigate Your
              <span className="gradient-text"> Career Ocean</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-12 leading-relaxed">
              OceanTech connects exceptional talent with world-class opportunities.
              Your next career move starts here.
            </p>


          </motion.div>
        </div>



        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-8">
              Your <span className="gradient-text">Voyage</span> to Success
            </h2>


            <div className="flex flex-col md:flex-row justify-center gap-6" data-testid="steps-section">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <Link to={step.href} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="glass-card rounded-2xl p-6 text-center hover:border-cyan-500/50 transition-colors group h-full"
                    >
                      <div className="w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-cyan-500" />
                      </div>
                      <div className="text-xl font-bold mb-2">{step.label}</div>
                      <div className="text-sm text-slate-400">{step.description}</div>
                    </motion.div>
                  </Link>
                );
              })}
            </div>

          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="features-heading">
              Why Choose <span className="gradient-text">OceanTech</span>
            </h2>
            <p className="text-lg text-slate-400">Professional recruitment services tailored to your success</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-testid="features-grid">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="glass-card rounded-3xl p-8 hover:border-cyan-500/50 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-cyan-500" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-slate-950">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-3xl p-12"
            data-testid="cta-section"
          >
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Ready to Dive In?
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Start your journey with OceanTech today. Find your dream job or hire exceptional talent.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <button className="btn-primary flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Discover Exceptional
                  <br />
                  Talent
                </button>
              </Link>
              <Link to="/jobs" data-testid="browse-jobs-btn">
                <button className="btn-primary flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Discover Career
                  <br />
                  Opportunities
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;